import { useState, useEffect, useCallback, useRef, useId, memo } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';

import { Radio } from './Radio';
import { Message } from '../Message';

import type { RadioGroupProps, RadioGroupOption, FontSizeValue } from './Radio.types';
import type { Theme } from '../../theme';

/**
 * Map theme font size token to actual value
 */
const getFontSizeValue = (fontSize: FontSizeValue | undefined, theme: Theme): string | undefined => {
  if (!fontSize) {
    return undefined;
  }
  
  // Check if it's a theme token key
  const themeTokens = ['xs', 'sm', 'base', 'md', 'lg', 'xl'] as const;
  if (themeTokens.includes(fontSize as typeof themeTokens[number])) {
    return theme.typography.fontSize[fontSize as keyof typeof theme.typography.fontSize];
  }
  
  // Otherwise, treat as CSS value
  return typeof fontSize === 'number' ? `${fontSize}px` : String(fontSize);
};

/**
 * Fieldset wrapper for the radio group
 */
const Fieldset = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
`;

/**
 * Legend for the radio group
 */
const Legend = styled.legend<{ theme?: Theme; $fontSize?: string }>`
  padding: 0;
  margin-bottom: 8px;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme, $fontSize }) => $fontSize || theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.grey[700]};
`;

/**
 * Required asterisk indicator
 */
const RequiredIndicator = styled.span<{ theme?: Theme }>`
  color: ${({ theme }) => theme.colors.highlight};
  margin-left: 2px;
`;

/**
 * Container for the radio options
 */
const GroupContainer = styled.div<{
  $orientation: 'horizontal' | 'vertical';
}>`
  display: flex;
  gap: 8px;
  flex-direction: ${({ $orientation }) =>
    $orientation === 'horizontal' ? 'row' : 'column'};
  flex-wrap: ${({ $orientation }) =>
    $orientation === 'horizontal' ? 'wrap' : 'nowrap'};
`;

/**
 * Container for messages
 */
const MessageContainer = styled.div`
  margin-top: 8px;
`;

/**
 * RadioGroup - A component for managing a group of radio buttons
 *
 * Handles selection state, keyboard navigation, and accessibility for a set of radio options.
 * Supports both controlled and uncontrolled modes.
 *
 * @example
 * ```tsx
 * // Controlled
 * <RadioGroup
 *   name="size"
 *   value={selectedSize}
 *   onChange={(value) => setSelectedSize(value)}
 *   options={[
 *     { id: 'sm', value: 'small', label: 'Small' },
 *     { id: 'md', value: 'medium', label: 'Medium' },
 *     { id: 'lg', value: 'large', label: 'Large' },
 *   ]}
 *   legend="Select Size"
 * />
 *
 * // Uncontrolled
 * <RadioGroup
 *   name="color"
 *   defaultValue="red"
 *   options={[
 *     { id: 'red', value: 'red', label: 'Red' },
 *     { id: 'blue', value: 'blue', label: 'Blue' },
 *   ]}
 * />
 * ```
 */
const RadioGroupComponent = ({
  name,
  value: controlledValue,
  defaultValue,
  onChange,
  options,
  disabled = false,
  error = false,
  orientation = 'vertical',
  legend,
  'aria-label': ariaLabel,
  required = false,
  errorMessage,
  assistiveMessage,
  className,
  'data-testid': testId,
  labelFontSize,
  optionsFontSize,
}: RadioGroupProps) => {
  const generatedId = useId();
  const groupId = name ? `radiogroup-${name}` : `radiogroup-${generatedId}`;
  const theme = useTheme() as Theme;
  
  // Resolve font sizes
  const resolvedLabelFontSize = getFontSizeValue(labelFontSize, theme);
  const resolvedOptionsFontSize = getFontSizeValue(optionsFontSize, theme);

  // Determine if controlled or uncontrolled
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const selectedValue = isControlled ? controlledValue : internalValue;

  // Refs for each radio option
  const radioRefs = useRef<Map<string | number | boolean, HTMLDivElement>>(
    new Map()
  );

  // Sync internal state with controlled value
  useEffect(() => {
    if (isControlled) {
      setInternalValue(controlledValue);
    }
  }, [isControlled, controlledValue]);

  const handleRadioChange = useCallback(
    (
      optionValue: string | number | boolean,
      event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
    ) => {
      if (!isControlled) {
        setInternalValue(optionValue);
      }
      onChange?.(optionValue, event);
    },
    [isControlled, onChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const { key } = event;

      // Determine navigation keys based on orientation
      const isVertical = orientation === 'vertical';
      const nextKey = isVertical ? 'ArrowDown' : 'ArrowRight';
      const prevKey = isVertical ? 'ArrowUp' : 'ArrowLeft';
      const altNextKey = isVertical ? 'ArrowRight' : 'ArrowDown';
      const altPrevKey = isVertical ? 'ArrowLeft' : 'ArrowUp';

      if (
        key === nextKey ||
        key === prevKey ||
        key === altNextKey ||
        key === altPrevKey
      ) {
        event.preventDefault();
        event.stopPropagation();

        // Get only enabled options
        const enabledOptions = options.filter(
          (option) => !option.disabled && !disabled
        );

        if (enabledOptions.length === 0) return;

        // Find current index
        const currentIndex = enabledOptions.findIndex(
          (option) => option.value === selectedValue
        );

        let nextIndex: number;

        if (key === nextKey || key === altNextKey) {
          // Move to next option (wrap around)
          nextIndex =
            currentIndex === -1 ? 0 : (currentIndex + 1) % enabledOptions.length;
        } else {
          // Move to previous option (wrap around)
          nextIndex =
            currentIndex === -1
              ? enabledOptions.length - 1
              : (currentIndex - 1 + enabledOptions.length) % enabledOptions.length;
        }

        const nextOption = enabledOptions[nextIndex];

        if (!nextOption) return;

        // Update selection
        handleRadioChange(
          nextOption.value,
          event as React.KeyboardEvent<HTMLDivElement>
        );

        // Move focus to the next radio
        setTimeout(() => {
          const radioRef = radioRefs.current.get(nextOption.value);
          radioRef?.focus();
        }, 0);
      }
    },
    [orientation, options, selectedValue, handleRadioChange, disabled]
  );

  const renderRadioOptions = (items: RadioGroupOption[]) => {
    // Determine which radio should be in tab order
    const selectedIndex = items.findIndex(
      (option) => option.value === selectedValue
    );
    const firstEnabledIndex = items.findIndex(
      (option) => !option.disabled && !disabled
    );

    // If nothing is selected, first enabled radio is in tab order
    const focusableIndex =
      selectedIndex !== -1 ? selectedIndex : firstEnabledIndex;

    return items.map((option, index) => {
      const isInTabOrder = index === focusableIndex;

      return (
        <Radio
          key={option.id}
          ref={(el) => {
            if (el) {
              radioRefs.current.set(option.value, el);
            } else {
              radioRefs.current.delete(option.value);
            }
          }}
          value={option.value}
          label={option.label}
          checked={option.value === selectedValue}
          disabled={option.disabled || disabled}
          error={option.error || error}
          name={groupId}
          tabIndex={isInTabOrder ? 0 : -1}
          onChange={(event) => handleRadioChange(option.value, event)}
          data-testid={testId && `${testId}-option-${option.id}`}
          labelFontSize={resolvedOptionsFontSize}
        />
      );
    });
  };

  return (
    <Fieldset className={className} data-testid={testId}>
      {legend && (
        <Legend $fontSize={resolvedLabelFontSize}>
          {legend}
          {required && <RequiredIndicator>*</RequiredIndicator>}
        </Legend>
      )}
      <GroupContainer
        role="radiogroup"
        aria-labelledby={legend ? groupId : undefined}
        aria-label={!legend ? ariaLabel : undefined}
        $orientation={orientation}
        onKeyDown={handleKeyDown}
        data-testid={testId && `${testId}-group`}
      >
        {renderRadioOptions(options)}
      </GroupContainer>
      {errorMessage && error && (
        <MessageContainer>
          <Message variant="error">{errorMessage}</Message>
        </MessageContainer>
      )}
      {assistiveMessage && !errorMessage && (
        <MessageContainer>
          <Message variant="assistive">{assistiveMessage}</Message>
        </MessageContainer>
      )}
    </Fieldset>
  );
};

RadioGroupComponent.displayName = 'RadioGroup';

export const RadioGroup = memo(RadioGroupComponent);

export default RadioGroup;
